/**
 * Mixin to add baggages collection observer functionality to a widget or an object.
 * To interact on each passenger update finished object must implement updatePassengerInfo function that
 * receives a passenger request data with next structure:
 *
 * includedBaggage: baggage is included
 * notBaggageAllowed: baggage is not allowed for this passenger
 * segmentBaggages: array of objects containing bags allowed for the passenger
 *      baggageType: not allowed, included or selectable
 *      segmentTypeIndex: FIRST, SECOND, THIRD, ....
 *      baggageApplicationLevel: SEGMENT or ITINERARY
 *      baggageDescriptorIncludedInPrice: contains info about included bags
 *      selectableBaggageDescriptor: contains info about selectable bags
 * baggagesSelectionType: indicates if baggages are for SEGMENT or ITINERARY
 * flightType: indicates flight type: one way, roundtrip, multitrip
 * FIRST: baggage selection for segment FIRST
 * SECOND: baggage selection for segment SECOND
 * THIRD: baggage selection for segment THIRD
 */
'use strict';
const baggagesOptionsCollection = require('../collections/baggages_row_options').getInstance();
const baggagesRowsCollection = require('../collections/baggages_rows').getInstance();
const baggagesCollectionBuilder = require('../builders/baggages');
const baggagesBuilderInstance = baggagesCollectionBuilder.getInstance();
const Mixin = require('./mixin');
const ITINERARY_SEGMENT_COUNT = 2;
const isMobile = Odigeo.SessionScope.flow === 'mobile';

module.exports = new Mixin({
    getBaggagesApplicationLevelEnum: function() {
        return baggagesBuilderInstance.getBaggagesApplicationLevelEnum();
    },
    getBaggagesRowIdsArray: function() {
        return baggagesBuilderInstance.getBaggagesRowIdsArray();
    },
    getBaggagesRequestTypesEnum: function() {
        return baggagesBuilderInstance.getBaggagesRequestTypesEnum();
    },
    subscribeToCollectionEvent: function(event, callback) {
        if (Odigeo._.isFunction(callback)) {
            this.listenTo(baggagesBuilderInstance, event, callback);
        }
    },
    suscribeOnCollectionBuildEvent: function() {
        const collectionBuiltEvent = 'collectionBuilt';
        this.listenTo(baggagesBuilderInstance, collectionBuiltEvent, this.onCollectionBuiltTrigger.bind(this));
    },
    onCollectionBuiltTrigger(baggageInfo) {
        const onCollectionBuilt = this.getEventHandlersHolder().onCollectionBuilt;
        if (Odigeo._.isFunction(onCollectionBuilt)) {
            onCollectionBuilt.call(this, baggageInfo);
        }

        this.suscribeOnBaggageChangeEvent();
        this.suscribeOnBaggageLoadedEvent();
    },
    suscribeOnBaggageChangeEvent: function() {
        const onBaggageChange = this.getEventHandlersHolder().onBaggageChange;

        if (Odigeo._.isFunction(onBaggageChange)) {
            Odigeo.bind('baggagesObserver:onBaggageChange', Odigeo._.bind(onBaggageChange, this));
        }
    },
    suscribeOnBaggageLoadedEvent: function() {
        const onBaggageLoaded = this.getEventHandlersHolder().onBaggageLoaded;

        if (Odigeo._.isFunction(onBaggageLoaded)) {
            Odigeo.bind('baggagesObserver:onBaggageLoaded', Odigeo._.bind(onBaggageLoaded, this));
        }
    },
    suscribeBaggagesCollectionEvents: function() {
        this.suscribeOnCollectionBuildEvent();

        const isBaggagesBuilderDefined = !Odigeo._.isUndefined(baggagesBuilderInstance);
        const isCollectionBuilt = baggagesBuilderInstance.isCollectionBuilt();
        const shouldTriggerCollectionBuilt = isBaggagesBuilderDefined && isCollectionBuilt;
        if (shouldTriggerCollectionBuilt) {
            this.onCollectionBuiltTrigger(baggagesBuilderInstance.baggagesInformation);
        }
    },
    getEventHandlersHolder: function() {
        return !Odigeo._.isEmpty(this.eventHandlers) ? this.eventHandlers : this;
    },
    // Baggages Options
    getBaggageOptionByCid: function(cid) {
        return baggagesOptionsCollection.get(cid);
    },
    getFirstBaggageOption: function(searchPattern) {
        return baggagesOptionsCollection.getOption(searchPattern);
    },
    getAllBaggagesOptions: function(searchPattern) {
        return baggagesOptionsCollection.getOptions(searchPattern);
    },
    getBaggagesRowSelection: function(idPassenger, idRow) {
        return baggagesOptionsCollection.getRowSelection(idPassenger, idRow);
    },
    getBaggagesRowIncluded: function(idPassenger, idRow) {
        return baggagesOptionsCollection.getRowIncluded(idPassenger, idRow);
    },
    getBaggagesPassengerSelection: function(passengerId) {
        return baggagesOptionsCollection.getPassengerSelection(passengerId);
    },
    getBaggageSelectedOptionsWithPieces: function() {
        return baggagesBuilderInstance.getBaggageSelectedOptionsWithPieces();
    },
    // Baggages Rows
    getBaggageBundleDiscountPercentage: function() {
        return baggagesRowsCollection.getBaggageBundleDiscountPercentage();
    },
    getBaggageRowByCid: function(cid) {
        return baggagesRowsCollection.get(cid);
    },
    getBaggageAllRows: function() {
        return baggagesRowsCollection.toJSON();
    },
    getBaggageAllRowsWithCid: function() {
        return Odigeo._.merge(Odigeo._.map(baggagesRowsCollection.models, function(model) {
            return {cid: model.cid};
        }), baggagesRowsCollection.toJSON());
    },
    getPassengerRows: function(idPassenger) {
        return baggagesRowsCollection.getPassengerRows(idPassenger);
    },
    getPassengerSelectableRows: function(idPassenger) {
        return baggagesRowsCollection.getPassengerSelectableRows(idPassenger);
    },
    getPassengerIncludedAndSelectableRows: function(idPassenger) {
        return baggagesRowsCollection.getPassengerIncludedAndSelectableRows(idPassenger);
    },
    getPassengerIncludedAndNoSelectableRows: function(idPassenger) {
        return baggagesRowsCollection.getPassengerIncludedAndNoSelectableRows(idPassenger);
    },
    getBaggageIncludedPieces: function() {
        return baggagesOptionsCollection.includedPieces;
    },
    getBaggageIncludedOptions() {
        return baggagesOptionsCollection.getOptions({
            isIncludedBaggage: true,
        });
    },
    getBaggageSelectedPieces: function() {
        return baggagesOptionsCollection.selectedPieces;
    },
    getPassengerBaggageSelectedPieces: (idPassenger) => baggagesOptionsCollection.getPassengerBaggageSelectedPieces(idPassenger),
    getTotalBaggageSelectedFee: function() {
        return Odigeo._.reduce(this.getBaggageSelectedOptionsWithPieces(), function(totalFee, option) {
            const segment = baggagesRowsCollection.getRow({idRow: option.get('idRow'), idPassenger: option.get('idPassenger')});
            const segmentFee = segment.get('baggageFeeIncludedInPrice') * option.get('piecesToShow');
            return totalFee + segmentFee;
        }, 0);
    },
    getTotalBaggageSelectedPieces: function() {
        let baggageSelectedPieces = this.getBaggageSelectedPieces();
        if (this.isAnItineraryTrip()) {
            baggageSelectedPieces *= ITINERARY_SEGMENT_COUNT;
        }
        return baggageSelectedPieces;
    },
    getSelectedBaggageAverageFee: function() {
        const baggageSelectedPieces = this.getTotalBaggageSelectedPieces();
        return baggageSelectedPieces > 0 ?
            Odigeo.Utils.formatCurrency(this.getTotalBaggageSelectedFee() / baggageSelectedPieces) : 0;
    },
    getPrimePriceFee: function() {
        const baggageSelectedPieces = this.getTotalBaggageSelectedPieces();
        const fee = this.getTotalBaggageSelectedFee();
        const [segment] = this.getBaggageSelectedOptionsWithPieces();
        let primeFee;
        if (segment) {
            const primePrice = segment.get('primePrice');
            const totalPrice = segment.get('price');
            const providerPrice = totalPrice - fee;
            primeFee = primePrice - providerPrice;
        }
        return baggageSelectedPieces > 0 ? Odigeo.Utils.formatCurrency(primeFee / baggageSelectedPieces) : 0;
    },
    getBaggagePropensity: function() {
        const firstBaggageSelectableRow = Odigeo._.first(this.getPassengerSelectableRows(0));
        return Odigeo._.isEmpty(firstBaggageSelectableRow) ? null : firstBaggageSelectableRow.get('baggagePropensity');
    },
    getDiscount: function() {
        return baggagesOptionsCollection.getDiscount();
    },
    getDiscountedPart: function() {
        return baggagesOptionsCollection.getDiscountedPart();
    },
    isBaggagePropensityLowForNag() {
        return baggagesOptionsCollection.isBaggagePropensityLowForNag();
    },
    isBaggagePropensityLowForMerchandisingDiscount() {
        return baggagesBuilderInstance.isBaggagePropensityLowForMerchandisingDiscount();
    },
    getBaggageSelectedTotalPrice: function() {
        return this.getBaggagesDiscountPrice(baggagesOptionsCollection.selectedTotalPrice);
    },
    getBaggagesInCartTotal: function() {
        return baggagesOptionsCollection.selectedInCartTotalPrice;
    },
    getBaggagesRequestType: function() {
        return baggagesBuilderInstance.getBaggagesRequestType();
    },
    getTripTypeId: function() {
        return baggagesBuilderInstance.getTripTypeId();
    },
    getSelectableRowsNoExtraBag: function() {
        return Odigeo._.filter(this.getBaggageAllRows(), function(row) {
            return !row.hasIncludedBaggages && row.hasSelectableBaggages;
        });
    },
    getFirstSelectableOptionsEachPax: function() {
        return Odigeo._.map(this.getSelectableRowsNoExtraBag(), function(row) {
            return Odigeo._.first(this.getAllBaggagesOptions({
                idPassenger: row.idPassenger,
                idRow: row.idRow,
                selected: false,
            }));
        }, this);
    },
    getFirstSelectableBaggageData: function() {
        const selectableOption = Odigeo._.first(this.getFirstSelectableOptionsEachPax());
        return !Odigeo._.isUndefined(selectableOption) ? selectableOption.pick('piecesToShow', 'kilos', 'price') : {};
    },
    evenUpBaggageSelection: function() {
        Odigeo._.forEach(this.getFirstSelectableOptionsEachPax(), function(paxOptions) {
            this.updateBaggageSelection({
                newOptionCid: paxOptions,
                isUserAction: true,
                triggerEvents: true,
            });
        }, this);
    },
    getPaxCount: function() {
        const searchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters')) || {};
        const adults = searchParams.numAdults || 1;
        const children = searchParams.numChildren || 0;
        return adults + children;
    },
    getTotalFRSegments: function() {
        return Odigeo._.filter(this.getBaggageAllRows(), (val) => {
            return val.idPassenger === 0 && (val.carrierCode === 'FR' || val.carrierCode === 'OE');
        }).length;
    },
    sendContinuanceBaggageTracking: function(page) {
        return baggagesBuilderInstance.sendContinuanceBaggageTracking(page);
    },

    doBaggageAllowAutoselection: function() {
        return baggagesBuilderInstance.doBaggageAllowAutoselection();
    },
    shouldDisplayGenericLogo: function() {
        return baggagesRowsCollection.shouldDisplayGenericLogo();
    },
    shouldDisplayPromotionBadge: function() {
        return this.isBaggagePropensityLowForMerchandisingDiscount() &&
            this.hasAnySelectableBaggage() &&
            baggagesOptionsCollection.getDiscountPercentage() > 0 &&
            this.getBaggageSelectedPieces() === 0;
    },
    shouldDisplayPrimeBadge(isPrimeView = false) {
        const hasBagsSelectableButNotSelected = this.hasAnySelectableBaggage() && this.getBaggageSelectedPieces() === 0;
        return isPrimeView && (!isMobile || hasBagsSelectableButNotSelected);
    },
    doBaggageAllowSmartNag: function() {
        const isAllowedTrip = baggagesRowsCollection.isAnItineraryTrip() || baggagesRowsCollection.isOneWayTrip();
        const doOptionsAllowSmartNag = baggagesOptionsCollection.doOptionsAllowSmartNag() || isAllowedTrip;
        return doOptionsAllowSmartNag && baggagesRowsCollection.doRowsAllowSmartNag() && !this.hasIncludedBaggage();
    },
    doBaggageAllowEvenUpNag: function() {
        const isAllowedBaggageType = this.hasIncludedBaggage() && this.hasSelectableRowsNoExtraBag();
        const isAllowedTrip = baggagesRowsCollection.isRoundTrip() && !baggagesRowsCollection.isAnItineraryTrip();
        return isAllowedTrip && isAllowedBaggageType;
    },
    /**
     * @param {number} passengerId Passenger id number
     * @return {Array<{ rowDescriptor: baggageSelectorDescriptor, options: Array<baggageOptionDescriptor> }>}
     */
    getBaggagePassengerComposedRowsInfo: function(passengerId) {
        const rowsDescriptors = this.getPassengerRows(passengerId);

        return Odigeo._.map(rowsDescriptors, function(rowInfo) {
            const idRow = rowInfo.get('idRow');
            const pattern = {idPassenger: passengerId, idRow: idRow};
            const options = this.sortOptions(this.getAllBaggagesOptions(pattern));

            return {
                rowDescriptor: rowInfo,
                options: options,
                selected: this.getBaggagesRowSelection(passengerId, idRow),
                included: this.getBaggagesRowIncluded(passengerId, idRow),
            };
        }, this);
    },
    getBaggagesDiscountPrice: function(total) {
        const discount = baggagesRowsCollection.getBaggageBundleDiscountPercentage();
        if (discount > 0 && baggagesOptionsCollection.areAllPassengersWithBaggage() && Odigeo.AB.isBaggageDiscountEnabled) {
            total = total - ((discount / 100) * total);
        }
        return total;
    },
    getBaggagesDiscount: function() {
        return baggagesRowsCollection.getBaggageBundleDiscountPercentage();
    },
    getAreAllPassengersWithBaggage: function() {
        return baggagesOptionsCollection.areAllPassengersWithBaggage();
    },
    hasAllRowsIncludedBaggages: function() {
        const rowsWithBaggagesIncluded = Odigeo._.filter(this.getBaggageAllRows(), {hasIncludedBaggages: true});
        return this.getBaggageAllRows().length > 0 && rowsWithBaggagesIncluded.length === this.getBaggageAllRows().length;
    },
    getAllBaggagePriceBreakDownItems: function() {
        return this.processBaggagePriceBreakDownItems(true);
    },
    getBaggagePriceBreakDownItems: function() {
        return this.processBaggagePriceBreakDownItems(false);
    },
    processBaggagePriceBreakDownItems: function(includedBaggage) {
        // Description of rows are same for all passengers, we get first passenger as instance.
        return Odigeo._.map(this.getPassengerRows(0), function(rowInfo) {
            const rowInfoDescriptor = rowInfo.toJSON();
            const patterns = [{
                idRow: rowInfoDescriptor.idRow,
                selected: true,
                isSelectableBaggage: true,
            }];

            if (includedBaggage) {
                patterns.push({
                    idRow: rowInfoDescriptor.idRow,
                    isIncludedBaggage: true,
                });
            }

            const baggage = this.getBaggageOptions(patterns);

            return Odigeo._.extend({}, {baggagesItems: baggage, size: this.getTotalPieces(baggage)},
                Odigeo._.pick(rowInfoDescriptor, 'departureIata', 'arrivalIata', 'baggageApplicationLevel', 'idRow'));
        }, this);
    },
    getBaggageOptions: function(patterns) {
        const baggage = Odigeo._.map(patterns, function(pattern) {
            return this.getAllBaggagesOptions(pattern);
        }, this);

        return this.getBaggageTotalByWeight(Odigeo._.flatten(baggage));
    },
    getTotalPieces: function(baggage) {
        return Odigeo._.reduce(baggage, function(total, item) {
            return total + item.pieces;
        }, 0);
    },
    getTotalPrice: function(baggage) {
        return Odigeo._.reduce(baggage, function(totalPrice, item) {
            return totalPrice + this.getTotalSegmentPrice(item.baggagesItems);
        }, 0, this);
    },
    getPriceBeforeDiscount: function(baggage) {
        return Odigeo._.reduce(baggage, function(totalPrice, item) {
            return totalPrice + this.getTotalSegmentPriceBeforeDiscount(item.baggagesItems);
        }, 0, this);
    },
    getTotalSegmentPrice: function(segmentBaggage) {
        return Odigeo._.reduce(segmentBaggage, function(baggagePrice, bag) {
            return baggagePrice + (!Odigeo._.isUndefined(bag.price) ? bag.price : 0);
        }, 0);
    },
    getTotalSegmentPriceBeforeDiscount: function(segmentBaggage) {
        return Odigeo._.reduce(segmentBaggage, function(baggagePrice, bag) {
            return baggagePrice + (!Odigeo._.isUndefined(bag.priceBeforeDiscount) ? bag.priceBeforeDiscount : 0);
        }, 0);
    },
    extendItineraryBaggage: function(baggage) {
        const baggageInfo = [];

        if (this.getFlightType() !== 'ROUND_TRIP') {
            return baggage;
        }

        Odigeo._.each(baggage, function(item) {
            baggageInfo.push(item);

            if (item.baggageApplicationLevel === 'ITINERARY' && item.idRow === 'FIRST') {
                baggageInfo.push(Odigeo._.extend(
                    Odigeo._.clone(item), {
                        arrivalIata: item.departureIata,
                        departureIata: item.arrivalIata,
                        idRow: 'SECOND',
                    }
                ));
            }
        });

        return baggageInfo;
    },
    getAccumulatedBaggageDescriptor: function(optionsArray) {
        return Odigeo._.reduce(optionsArray, function(accum, option) {
            return {
                pieces: accum.pieces + this.getOptionPieces(option),
                kilos: option.get('kilos'),
                kilosPerPiece: option.get('kilosPerPiece'),
                length: accum.length || option.get('pieces'),
                price: accum.price + option.get('price'),
            };
        }, {pieces: 0, kilos: 0, kilosPerPiece: 0, price: 0, length: 0}, this);
    },
    getOptionPieces: function(option) {
        return option.get('isIncludedBaggage') ? option.get('pieces') : option.get('piecesToShow');
    },
    getBaggageTotalByWeight: function(selectedBaggage) {
        const baggageGroupedByWeight = this.getBaggageGroupedByWeight(selectedBaggage);
        const processedBaggage = this.accumulateBaggageTotalByWeight(baggageGroupedByWeight);

        return processedBaggage.length > 0 ? processedBaggage : [{
            pieces: 0,
            kilos: 0,
        }];
    },
    hasTrainSegment: function() {
        return baggagesBuilderInstance.hasTrainSegment();
    },
    isPureTrain: function() {
        return baggagesBuilderInstance.isPureTrain();
    },
    accumulateBaggageTotalByWeight: function(baggage) {
        let accumulatedBaggage = {
            pieces: 0,
            kilos: true,
            price: 0,
            priceBeforeDiscount: 0,
        };

        let groupedBaggage = Odigeo._.reduce(baggage, function(outputArray, optionsArray) {
            const descriptor = this.getAccumulatedBaggageDescriptor(optionsArray);

            if (descriptor.pieces !== 0) {
                outputArray.push(descriptor);
                accumulatedBaggage = this.getCompoundWeightBaggageOption(accumulatedBaggage, descriptor);
            }

            return outputArray;
        }, [], this);

        accumulatedBaggage.priceBeforeDiscount = accumulatedBaggage.price;
        accumulatedBaggage.price = this.getBaggagesDiscountPrice(accumulatedBaggage.price);

        if (accumulatedBaggage.kilos && groupedBaggage.length > 0) {
            groupedBaggage = Odigeo._.map(groupedBaggage, (baggage) => {
                baggage.priceBeforeDiscount = baggage.price;
                baggage.price = this.getBaggagesDiscountPrice(baggage.price);
                return baggage;
            });
        }

        return accumulatedBaggage.kilos ? groupedBaggage : [{
            pieces: accumulatedBaggage.pieces,
            kilos: 0,
            length: 1,
            price: accumulatedBaggage.price,
            priceBeforeDiscount: accumulatedBaggage.priceBeforeDiscount,
        }];
    },
    getCompoundWeightBaggageOption: function(accumulated, descriptor) {
        return {
            pieces: accumulated.pieces + descriptor.pieces,
            kilos: accumulated.kilos && descriptor.kilos > 0,
            price: accumulated.price + descriptor.price,
        };
    },
    getBaggageGroupedByWeight: function(baggage) {
        return Odigeo._.groupBy(baggage, function(option) {
            return option.get('kilosPerPiece');
        });
    },
    calculateTotalQuantity: function() {
        return this.getBaggageIncludedPieces() + this.getBaggageSelectedPieces();
    },
    hasPassengerSelectableBaggage: function() {
        const selectableOption = this.getFirstBaggageOption({
            idPassenger: this.options.paxIndex,
            isSelectableBaggage: true,
        });
        return !Odigeo._.isUndefined(selectableOption);
    },
    hasAnySelectableBaggage: function() {
        const selectableOption = this.getFirstBaggageOption({
            isSelectableBaggage: true,
            selected: false,
        });
        return !Odigeo._.isUndefined(selectableOption);
    },
    hasIncludedBaggage: function() {
        const includedOption = this.getFirstBaggageOption({
            isIncludedBaggage: true,
        });
        return !Odigeo._.isUndefined(includedOption);
    },
    hasSelectableRowsNoExtraBag: function() {
        return Odigeo._.size(this.getSelectableRowsNoExtraBag()) > 0;
    },
    haveAllSegmentsBagsSelected: function() {
        return baggagesBuilderInstance.haveAllSegmentsBagsSelected();
    },
    hasPassengerAllSegmentsWithIncludedAndSelectableBaggage: function() {
        const options = this.getPassengerRows(this.options.paxIndex);
        const includedAndSelectableOptions = this.getPassengerIncludedAndSelectableRows(this.options.paxIndex);

        return (
            !Odigeo._.isUndefined(options) &&
            !Odigeo._.isUndefined(includedAndSelectableOptions) &&
            options.length === includedAndSelectableOptions.length
        );
    },
    hasNoBagBeenSelected: function() {
        return baggagesOptionsCollection.selectedPieces === 0;
    },
    isAnItineraryTrip: function() {
        return baggagesRowsCollection.isAnItineraryTrip();
    },
    isOneWayTrip: function() {
        return baggagesRowsCollection.isOneWayTrip();
    },
    getFlightType: function() {
        return baggagesRowsCollection.getRequestType();
    },
    sortOptions: function(options) {
        let noBaggageOption;
        const unsortedArray = Odigeo._.reject(options, function(option) {
            const pieces = option.get('pieces');
            const isNoPiecesOption = pieces === 0;

            if (isNoPiecesOption) {
                noBaggageOption = option;
            }

            return isNoPiecesOption;
        });
        const sortedArray = Odigeo._.sortBy(unsortedArray, function(option) {
            return option.get('pieces');
        });

        if (!Odigeo._.isUndefined(noBaggageOption)) {
            sortedArray.push(noBaggageOption);
        }

        return sortedArray;
    },
    setPrimeInfo: (primeInfo) => baggagesOptionsCollection.setPrimeInfo(primeInfo),
    getPrimeInfo: () => baggagesOptionsCollection.getPrimeInfo(),
});
